import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Formik, FormikValues } from 'formik';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';

import { popDialog, showTaskRoutesDialog } from '../../../../state/ui/dialog';

import DialogTitle from '../../../dialogs/DialogTitle';

import { useAppDispatch } from '../../../../hooks';
import useToggle from '../../../../hooks/useToggle';
import TranslationHelper from '../../../../helpers/TranslationHelper';

import { useStyles } from './Themable.hooks';
import { usePrivileges } from '../../../../state/auth/index.hooks';
import { createTask, updateTask } from '../../../../state/app/tasks';
import {
    createTaskForm,
    prepareTask,
} from '../../../../features/TaskForm/utils';
import { TaskModificationForm } from './components/TaskModificationForm';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
    EUpdateTaskErrorCode,
    IEditTaskDialogFormValues,
    ITaskModificationDialogParams,
    IUpdateTaskError,
} from './types';
import { useTaskStoredForm } from '../../../../selectors/forms';
import { IEditTask } from '../../../../services/tasks';
import { clearForm } from '../../../../state/ui/forms';
import { taskSchema } from '../../../../schemas';

import { useTaskFormInitializer } from '../../../../features/TaskForm/hooks/useTaskFormInitializer';
import { fetchTask } from '../../../../state/_actions';
import { validateAllErrors } from '../../../../features/Attributes/utils/validation';

interface IOwnProps {
    dialogData: ITaskModificationDialogParams;
}
let setFieldValueRef: (field: string, value: any) => void;

const TaskModificationDialog = ({ dialogData }: IOwnProps) => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();

    const privileges = usePrivileges();
    const storedResult = useTaskStoredForm();

    const formState = useTaskFormInitializer(dialogData);
    const { isOpen, handleToggle } = useToggle(true);
    const [loading, setLoading] = useState(false);

    const { taskId, routeAssigned, mode } = dialogData;

    const initialValues = { data: storedResult || formState, attributes: {} };
    const handleClose = (event?: object, reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        dispatch(popDialog());
        dispatch(clearForm({ type: 'task' }));
        handleToggle();
    };
    const showSnackBarOnFailure = (message: string, onError: any) => {
        onError();
        enqueueSnackbar(message, { variant: 'error' });
        handleClose();
        return undefined;
    };

    const showDialogOnFailure = (values: FormikValues, onError: any) => () => {
        onError();
        return showTaskRoutesDialog({
            filter: {
                activityCategoryId: values.data.activityCategory,
                employeeId: values.data.employee.id,
                vehicleId: values.data.vehicle.id,
                date: values.data.plannedOn,
            },
            setFieldValue: setFieldValueRef,
        });
    };

    const handleOnSuccess = () => {
        dialogData.onSuccess();
        taskId && dispatch(fetchTask(taskId));
        handleClose();
    };

    const createFailureAction = (
        privileges: any,
        values: FormikValues,
        onError: () => void
    ) => {
        return (error: IUpdateTaskError) => {
            if (error.status === 409) {
                const entity = error.responseJSON?.entity;
                const errorCode = entity?.errorCode;
                switch (errorCode) {
                    case EUpdateTaskErrorCode.TOO_MANY_ROUTES:
                        return privileges.routes
                            ? dispatch(showDialogOnFailure(values, onError))
                            : showSnackBarOnFailure(
                                  TranslationHelper.translate(
                                      'No sufficient rights'
                                  ),
                                  onError
                              );
                    case EUpdateTaskErrorCode.EXISTING_RFID_ON_DATE:
                        return showSnackBarOnFailure(
                            TranslationHelper.translate(entity?.errorMessage),
                            onError
                        );
                    default:
                        return undefined;
                }
            }
            return showSnackBarOnFailure(
                TranslationHelper.translate(
                    'An error has occurred. Check the data you have entered.'
                ),
                onError
            );
        };
    };

    const handleAddSubmit = (values: FormikValues, onError: () => void) => {
        const files: File[] =
            (values.data.attachments && values.data.attachments.files) || [];
        dispatch(
            createTask(
                createTaskForm(
                    prepareTask(values.data, values.attributes),
                    files
                ),
                handleOnSuccess,
                createFailureAction(privileges, values, onError)
            )
        );
    };

    const handleEditSubmit = (values: FormikValues, onError: () => void) => {
        if (taskId) {
            const newTask: IEditTask = {
                id: taskId,
                ...prepareTask(values.data, values.attributes),
            };
            const files: File[] =
                (values.data.attachments && values.data.attachments.files) ||
                [];
            const form = createTaskForm(newTask, files);
            form.append('id', taskId.toString());
            dispatch(
                updateTask(
                    form,
                    handleOnSuccess,
                    createFailureAction(privileges, values, onError)
                )
            );
        }
    };

    const onError = () => {
        setLoading(false);
    };
    const handleSubmit = async (
        values: IEditTaskDialogFormValues,
        { setErrors }: any
    ) => {
        const schema = taskSchema();
        const allErrors = await validateAllErrors(
            schema,
            values.attributes,
            values
        );

        if (allErrors) {
            setErrors(allErrors);
            return;
        }
        setLoading(true);
        if (mode === 'add' || mode === 'copy') {
            return handleAddSubmit(values, onError);
        }
        if (mode === 'edit') {
            return handleEditSubmit(values, onError);
        }
        return;
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(values, formikHelpers) =>
                handleSubmit(values, formikHelpers)
            }
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={{}}
        >
            {({ errors, values, setFieldValue, submitForm }) => {
                setFieldValueRef = setFieldValue;
                return (
                    <Dialog
                        open={isOpen}
                        onClose={handleClose}
                        maxWidth={'md'}
                        disableEscapeKeyDown
                        classes={{}}
                    >
                        <DialogTitle
                            title={TranslationHelper.translate(
                                mode === 'edit' ? 'Edit task' : 'Add task'
                            )}
                            save={submitForm}
                            close={handleClose}
                            loading={loading}
                        />
                        <DialogContent className={classes.dialogContent}>
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                                <TaskModificationForm
                                    mode={mode}
                                    formValues={values}
                                    errors={errors}
                                    loading={loading}
                                    setLoading={setLoading}
                                    setFieldValue={setFieldValue}
                                    handleAddLocation={
                                        dialogData?.handleAddLocation
                                    }
                                    routeAssigned={routeAssigned}
                                    taskId={taskId}
                                />
                            </MuiPickersUtilsProvider>
                        </DialogContent>
                    </Dialog>
                );
            }}
        </Formik>
    );
};

export default TaskModificationDialog;
